import React, { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import MoneyInput from "@/components/MoneyInput";
import InfoTip from "@/components/onboarding/InfoTip";
import CollapsibleCard from "@/components/onboarding/CollapsibleCard";
import VatLearnMore from "@/components/onboarding/VatLearnMore";
import { useOpenCard } from "@/components/onboarding/useOpenCard";
import { RegularCostFields, ContingencyFields, ContingencyChoice } from "@/components/onboarding/RegularCostFields";
import { regularHeader, costComplete, monthNames, contingencyAmount } from "@/lib/costHelpers";
import { formatGBP } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const P = ({ children }) => <p className="text-black/80 leading-relaxed">{children}</p>;

export const RegularCostsStep = ({ costs, startIso, vat, makeItem, onAdd, onPatch, onRemove, contingencyAsked, onOpenContingency }) => {
  const card = useOpenCard(costs, costComplete);
  const hasContingency = costs.some((c) => c.is_contingency);
  return (
    <div className="space-y-5" data-testid="regular-costs-step">
      <P>What does it cost to run your business, again and again? Open your banking app and scroll through the last 2–3 months. Note everything that already goes out regularly, e.g. software, memberships, office rent, your VA, and yearly bills like insurance too.</P>
      <P>Then look ahead: will anything new start in the next 12 months? A new tool for a launch, or six months of ads? Add those too.</P>
      <ul className="list-disc pl-5 space-y-2 text-black/80 leading-relaxed">
        <li>For each cost, use <strong>How often?</strong> to choose the frequency, e.g. monthly, quarterly, twice a year or once a year, or pick custom months &amp; amounts.</li>
        <li>Something you pay every few years, like a domain renewal? If it's due in the next 12 months, add it as a one-off cost in the next step instead.</li>
        <li>Not sure of the exact figure? A sensible estimate is absolutely fine. Once your forecast is built, you'll record what really happened each month, and your forecast gets sharper as you go.</li>
      </ul>
      <div className="card bg-[#FFF7E5] !border-[#FFBD4A]" data-testid="tax-bills-tip">
        <p className="font-semibold mb-2">Don't forget your tax bills. Add them here as regular costs:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm leading-relaxed">
          <li><strong>Limited company:</strong> your Corporation Tax bill</li>
          <li><strong>Sole trader or company director:</strong> your Self Assessment payments, usually due 31 January and 31 July. Use <em>Custom months &amp; amounts</em> if the two differ.</li>
        </ul>
        <p className="text-sm mt-2">Not sure of the amount? Use last year's bill as a guide, or add a sensible estimate.</p>
      </div>
      <div className="ai-insight">
        <strong>Heads up:</strong> this step is for regular recurring running costs. One-off costs and your own pay come in the next steps.
      </div>
      <VatLearnMore vat={vat} testid="recurring-vat" />

      {costs.length === 0 && <p className="text-black/60" data-testid="recurring-empty">No regular costs yet.</p>}
      {costs.map((c, i) => (
        <CollapsibleCard
          key={c.id}
          testid={`recurring-row-${i}`}
          title={regularHeader(c, i, startIso, costs)}
          open={card.openId === c.id}
          nudge={card.nudgeId === c.id}
          onToggle={() => card.toggle(c.id)}
          onRemove={() => onRemove(i)}
        >
          {c.is_contingency ? (
            <ContingencyFields c={c} costs={costs} onPatch={(p) => onPatch(i, p)} />
          ) : (
            <RegularCostFields c={c} i={i} startIso={startIso} onPatch={(p) => onPatch(i, p)} />
          )}
        </CollapsibleCard>
      ))}
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" className="btn-secondary" onClick={() => card.tryAdd(makeItem(), onAdd)} data-testid="recurring-add">
          <Plus size={16} weight="bold" /> Add {costs.length ? "another" : "a"} regular cost
        </button>
        {contingencyAsked && !hasContingency && (
          <button type="button" className="text-sm underline font-semibold" onClick={onOpenContingency} data-testid="contingency-add-link">
            Add a contingency
          </button>
        )}
      </div>
    </div>
  );
};

export const ContingencyDialog = ({ open, costs, onAdd, onDecline }) => {
  const [c, setC] = useState({ contingency_mode: "fixed", amount: 0, contingency_pct: "" });
  const preview = contingencyAmount(costs, { ...c, contingency_mode: "pct" });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDecline(false)}>
      <DialogContent className="max-w-lg bg-white border-[1.5px] border-black rounded-2xl" data-testid="contingency-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">Want to add a buffer for the unexpected?</DialogTitle>
          <DialogDescription className="text-black/80 text-base leading-relaxed pt-2">
            Most businesses get surprise costs: a broken laptop, a price rise, a forgotten renewal. A small monthly contingency means they don't knock you off course.
          </DialogDescription>
        </DialogHeader>
        <ContingencyChoice mode={c.contingency_mode} pct={c.contingency_pct} amount={c.amount} preview={preview} onChange={(p) => setC((s) => ({ ...s, ...p }))} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
          <button type="button" className="btn-primary" onClick={() => onAdd(c)} data-testid="contingency-add-btn">Add contingency</button>
          <button type="button" className="text-sm underline font-semibold" onClick={() => onDecline(true)} data-testid="contingency-decline-btn">No thanks</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const oneOffComplete = (c) => !!(c.name || "").trim() && Number(c.amount) > 0;

export const OneOffCostsStep = ({ costs, startIso, vat, makeItem, onAdd, onPatch, onRemove }) => {
  const card = useOpenCard(costs, oneOffComplete);
  const months = monthNames(startIso, true);
  return (
    <div className="space-y-5" data-testid="one-off-step">
      <P>Look ahead: is anything coming up that you'll only pay for once? For example:</P>
      <ul className="list-disc pl-5 space-y-1 text-black/80 leading-relaxed">
        <li>a new laptop</li>
        <li>a course</li>
        <li>a website build or a brand photoshoot</li>
        <li>something you pay every few years, like a domain renewal.</li>
      </ul>
      <P>For each one that's due within the next 12 months, add roughly how much it'll be and which month it's likely to land in. Best guesses are absolutely fine.</P>
      <P>This is where your forecast really earns its keep: it shows you <em>now</em> whether a bigger cost could leave you short later, so you can plan for it instead of being caught out.</P>
      <P>Nothing coming up? No problem. Remove the card and carry on.</P>
      <VatLearnMore vat={vat} testid="oneoff-vat" />

      {costs.length === 0 && <p className="text-black/60" data-testid="oneoff-empty">No one-off costs yet.</p>}
      {costs.map((c, i) => (
        <CollapsibleCard
          key={c.id}
          testid={`oneoff-row-${i}`}
          title={`${c.name || `One-off cost ${i + 1}`} · ${months[Number(c.month_index) || 0]} · ${formatGBP(Number(c.amount) || 0)}`}
          open={card.openId === c.id}
          nudge={card.nudgeId === c.id}
          onToggle={() => card.toggle(c.id)}
          onRemove={() => onRemove(i)}
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">What is it?</label>
              <input className="input" data-testid={`oneoff-name-${i}`} value={c.name} onChange={(e) => onPatch(i, { name: e.target.value })} placeholder="e.g. New laptop" maxLength={120} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                Amount (£)
                <InfoTip testid={`oneoff-amount-tip-${i}`}>As it leaves your bank, including VAT if you pay it.</InfoTip>
              </label>
              <MoneyInput value={c.amount} onChange={(n) => onPatch(i, { amount: n })} testid={`oneoff-amount-${i}`} placeholder="1,200" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                In which month?
                <InfoTip testid={`oneoff-month-tip-${i}`}>Only add a one-off cost if it's due in the next 12 months. Not sure exactly when? Pick your best guess; you can move it later.</InfoTip>
              </label>
              <select className="input" data-testid={`oneoff-month-${i}`} value={Number(c.month_index) || 0} onChange={(e) => onPatch(i, { month_index: Number(e.target.value) })}>
                {months.map((m, k) => (
                  <option key={k} value={k}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </CollapsibleCard>
      ))}
      <button type="button" className="btn-secondary" onClick={() => card.tryAdd(makeItem(), onAdd)} data-testid="oneoff-add">
        <Plus size={16} weight="bold" /> Add {costs.length ? "another" : "a"} one-off cost
      </button>
    </div>
  );
};
